import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLink } from 'react-icons/fi';
import { colors } from '../theme/colors';
import { useAuth } from '../context/AuthContext';
import { navigateToTransaction } from '../utils/navigationHelper';
import { fetchDocNumbers, knownDocNumber } from '../utils/docNumberLookup';

/**
 * The numbers of linked documents (e.g. the Sale Orders made from a
 * Quotation), each one clickable to open it — instead of just a count.
 *
 *   <LinkedDocNumbers ids={qt.childIds} />                 // list row
 *   <LinkedDocNumbers ids={so.childIds} max={0} />         // view: show all
 *
 * `max` (default 3) limits how many are listed; the rest show as "+N more"
 * (all of them in the tooltip). Numbers are looked up once per device — see
 * utils/docNumberLookup.js.
 */
const LinkedDocNumbers = ({ ids, known, max = 3, fallback = null, showIcon = true, color = colors.primary.main, style }) => {
  const navigate = useNavigate();
  const { activeCompany } = useAuth();
  const companyId = activeCompany?.id || null;
  const list = [...new Set((Array.isArray(ids) ? ids : []).filter(Boolean))];
  const idsKey = list.join(',');
  const [doneKey, setDoneKey] = useState('');

  const knownKey = known ? Object.keys(known).join(',') : '';

  useEffect(() => {
    let alive = true;
    if (companyId && idsKey) {
      // Numbers the document already carries (e.g. its parent's) need no lookup.
      const need = idsKey.split(',').filter((id) => !(known && known[id] && known[id].n));
      fetchDocNumbers(companyId, need).then(() => { if (alive) setDoneKey(idsKey); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, idsKey, knownKey]);

  if (list.length === 0) return fallback;

  const done = doneKey === idsKey;
  const items = list
    .map((id) => ({ id, info: (known && known[id] && known[id].n) ? known[id] : knownDocNumber(companyId, id) }))
    .filter((x) => x.info || !done); // after the lookup, a deleted document is simply left out
  if (items.length === 0) return fallback;

  const shown = max > 0 ? items.slice(0, max) : items;
  const more = items.length - shown.length;
  const allText = items.map((x) => (x.info ? x.info.n : '…')).join(', ');

  const open = (e, x) => {
    e.stopPropagation();
    if (x.info && x.info.t) navigateToTransaction(navigate, x.info.t, x.id);
  };

  return (
    <span
      title={allText}
      style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, fontSize: 12, ...style }}
    >
      {showIcon && <FiLink size={11} style={{ color: colors.text.secondary, flexShrink: 0 }} />}
      {shown.map((x, i) => (
        <span key={x.id}>
          {x.info ? (
            <span
              onClick={(e) => open(e, x)}
              style={{ cursor: x.info.t ? 'pointer' : 'default', color, fontWeight: 600, whiteSpace: 'nowrap' }}
              onMouseEnter={(e) => { if (x.info.t) e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              {x.info.n}
            </span>
          ) : (
            <span style={{ color: colors.text.tertiary }}>…</span>
          )}
          {i < shown.length - 1 ? ',' : ''}
        </span>
      ))}
      {more > 0 && <span style={{ color: colors.text.secondary }}>+{more} more</span>}
    </span>
  );
};

/**
 * Props for a document's parent link(s): its stored parent number needs no
 * lookup; extra parents (an invoice made from several challans) are looked up.
 *   <LinkedDocNumbers {...parentLinks(inv, 'DC')} />
 */
export const parentLinks = (d, defaultType = '') => {
  if (!d) return { ids: [] };
  const ids = (Array.isArray(d.parentIds) && d.parentIds.length > 0) ? d.parentIds : (d.parentId ? [d.parentId] : []);
  const known = (d.parentId && d.parentDocNumber)
    ? { [d.parentId]: { n: d.parentDocNumber, t: d.parentTranType || defaultType } }
    : undefined;
  return { ids, known };
};

export default LinkedDocNumbers;
